import React, { useRef } from 'react';
import { useSelector } from 'react-redux';
import FlowPageLayout from '../../components/layout/FlowLayout';
import { FLOWS_ENUM } from '../../common/enums';
import {
  selectConfigExpressLocalContactVerbiage,
  selectConfigExpressRegistrationLocalContactOptionalFields,
  selectConfigExpressRegistrationPeopleLocalContactDescription,
  selectConfigExpressRegistrationPeoplePropertyManagementDescription,
  selectConfigExpressRegistrationPropertyManagerEnabled,
  selectConfigExpressRegistrationPropertyManagerOptionalFields,
} from '../../store/slices/config/configSelectors';
import { ApplicationData } from 'src/types/flow';
import {
  setCurrentStep,
  setStepCompletedStatus,
} from 'src/store/slices/flow/flowSlice';
import usePlaceNavigate from 'src/hooks/usePlaceNavigate';
import {
  selectCurrentStep,
  selectNextStepPath,
} from 'src/store/slices/flow/flowSelectors';
import { ContactForm, ContactFormRef } from 'src/components/form/ContactForm';
import { useAppDispatch } from 'src/store/store';

interface ContactsPageProps {
  data: ApplicationData;
  flow: FLOWS_ENUM;
  updateApplicationData: (data: ApplicationData) => void;
}

export const ContactsPage = ({
  data,
  flow,
  updateApplicationData,
}: ContactsPageProps) => {
  const contactFormRef = useRef<ContactFormRef>(null);
  const dispatch = useAppDispatch();
  const placeNavigate = usePlaceNavigate();

  const { registrant } = data as ApplicationData;

  const selectedPropertyManagerEnabled = useSelector(
    selectConfigExpressRegistrationPropertyManagerEnabled
  );
  const selectedPropertyManagerOptionalFields = useSelector(
    selectConfigExpressRegistrationPropertyManagerOptionalFields
  );
  const selectedLocalContactOptionalFields = useSelector(
    selectConfigExpressRegistrationLocalContactOptionalFields
  );
  const propertyManagerDescription = useSelector(
    selectConfigExpressRegistrationPeoplePropertyManagementDescription
  );
  const localContactDescription = useSelector(
    selectConfigExpressRegistrationPeopleLocalContactDescription
  );
  const selectedLocalContactVerbiage = useSelector(
    selectConfigExpressLocalContactVerbiage
  );

  const selectedNextStepPath = useSelector(selectNextStepPath);
  const selectedCurrentStep = useSelector(selectCurrentStep);

  const updateData = () => {
    const data = contactFormRef.current?.getValues() as ApplicationData;
    updateApplicationData(data);
  };

  const handleNextClick = async () => {
    if (!registrant?.registrantType && flow !== FLOWS_ENUM.ADMIN_UPDATE) return;
    if (contactFormRef.current?.validateForm()) {
      updateData();
      dispatch(
        setStepCompletedStatus({ stepNum: selectedCurrentStep, status: true })
      );
      dispatch(setCurrentStep(selectedCurrentStep + 1));
      placeNavigate('/' + selectedNextStepPath);
    }
  };

  const handleBackClick = () => {
    updateData();
  };

  return (
    <FlowPageLayout
      flow={flow}
      title="Property Contacts"
      onNext={handleNextClick}
      onBack={handleBackClick}
      omitNavigationForward
      showNext
      showBack
    >
      <ContactForm
        data={data as ApplicationData}
        localContactData={{
          description: localContactDescription,
          verbiage: selectedLocalContactVerbiage,
          optionalFields:
            selectedLocalContactOptionalFields as unknown as Record<
              string,
              unknown
            >,
          enabled: true,
        }}
        propertyManagerData={{
          description: propertyManagerDescription,
          optionalFields:
            selectedPropertyManagerOptionalFields as unknown as Record<
              string,
              unknown
            >,
          enabled: selectedPropertyManagerEnabled,
        }}
        isAdminUpdate={flow === FLOWS_ENUM.ADMIN_UPDATE}
        ref={contactFormRef}
      />
    </FlowPageLayout>
  );
};
